import { buildDiscoverUrl } from "@/app/[locale]/_lib/urlBuilders";
import type { LocaleType } from "@/constants/locales";
import { RESERVED_DISCOVER_SEGMENTS } from "@/constants/reservedSlugs";
import { useSearchTriggerContext } from "@/contexts/SearchTriggerContext";
import { trackSearchSubmitted } from "@/lib/analytics/discoverContext";
import { useLocale } from "next-intl";
import pendingSearch from "@/lib/pendingSearch";
import { getCountryDefaultArea, getLocaleCountry } from "@/utils/location";
import { slugify } from "@/utils/slugs";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useTransition } from "react";

const STORAGE_KEY = "discover-search-state";

function parseDiscoverRoute(pathname: string): {
  locationSlug?: string;
  activitySlug?: string;
} {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] === "discover") {
    parts.shift();
  }

  return {
    locationSlug: parts[1],
    activitySlug: parts[2],
  };
}

function getStoredListingLocationSlug(locale: string): string | undefined {
  if (typeof window === "undefined") return undefined;

  const storedState = sessionStorage.getItem(STORAGE_KEY);
  if (!storedState) return undefined;

  const [storedPath] = storedState.split("?");
  const parts = storedPath.split("/").filter(Boolean);
  const storedLocale = parts[0];
  const storedLocation = parts[1];

  if (!storedLocation || storedLocale !== locale) return undefined;
  if (RESERVED_DISCOVER_SEGMENTS.has(storedLocation)) return undefined;

  return storedLocation;
}

type PendingSearchState = ReturnType<typeof pendingSearch.get>;

/** Params owned by the route slugs; `f` is the legacy bundled blob, never re-written. */
const SLUG_OWNED_PARAMS = new Set([
  "area",
  "lat",
  "lon",
  "locationName",
  "f",
  "activities",
]);

function resolveLocationSlug(
  { area, customLocation }: PendingSearchState,
  fallbacks: {
    routeLocation: string | undefined;
    storedListingLocation: string | undefined;
    fallbackArea: string | undefined;
    locale: LocaleType;
  },
): string {
  if (customLocation) return slugify(customLocation.displayName);
  if (area) return area;
  // On detail routes (/events|/clubs), preserve last listing location from session state.
  return (
    fallbacks.routeLocation ||
    fallbacks.storedListingLocation ||
    fallbacks.fallbackArea ||
    getCountryDefaultArea(getLocaleCountry(fallbacks.locale))
  );
}

function resolveActivitySlug(
  activities: PendingSearchState["activities"],
  routeActivitySlug: string | undefined,
): string | undefined {
  if (activities === undefined) return routeActivitySlug;
  if (Array.isArray(activities) && activities.length > 0) {
    return slugify(activities[0]);
  }
  return undefined;
}

function preservedParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams();
  params.forEach((value, key) => {
    if (!SLUG_OWNED_PARAMS.has(key)) next.set(key, value);
  });
  return next;
}

export type UseApplyPendingSearchOptions = {
  /**
   * If true, navigates to homepage with locale prefix after applying search.
   * If false, updates current page URL with new search params.
   * @default false
   */
  redirectToHome?: boolean;
  /**
   * Preferred area when current pathname doesn't contain a discover location slug.
   * Used on detail pages where redirecting "home" should keep nearby context.
   */
  fallbackArea?: string;
};

/**
 * Returns a stable callback that applies the pending search state to URL parameters.
 *
 * @param options - Configuration options
 * @param options.redirectToHome - Whether to redirect to homepage (detail pages) or update current page (homepage)
 * @returns Object containing apply function and isPending loading state
 *
 * @example
 * // On homepage - update current URL
 * const { apply } = useApplyPendingSearch();
 *
 * @example
 * // On detail page - redirect to homepage
 * const { apply, isPending } = useApplyPendingSearch({ redirectToHome: true });
 */
export function useApplyPendingSearch(
  options: UseApplyPendingSearchOptions = {},
) {
  const { redirectToHome = false, fallbackArea } = options;
  const params = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const locale = useLocale();
  const { resetAllTriggers } = useSearchTriggerContext();
  const [isPending, startTransition] = useTransition();
  // The draft this instance submitted. Each mounted search button has its own.
  // get() returns the same object until a write, so a later edit is a new one.
  const appliedRef = useRef<PendingSearchState | null>(null);

  useEffect(() => {
    if (isPending || !appliedRef.current) return;
    if (pendingSearch.get() === appliedRef.current) {
      pendingSearch.clear();
    }
    appliedRef.current = null;
  }, [isPending]);

  useEffect(() => {
    return () => {
      if (appliedRef.current && pendingSearch.get() === appliedRef.current) {
        pendingSearch.clear();
      }
      appliedRef.current = null;
    };
  }, []);

  const apply = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      e?.stopPropagation();
      // Only the Search buttons call apply.
      trackSearchSubmitted();

      const pending = pendingSearch.get();
      const parsedPath = parseDiscoverRoute(pathname);
      const routeLocationSlug = parsedPath.locationSlug;
      const isReservedRouteLocation = routeLocationSlug
        ? RESERVED_DISCOVER_SEGMENTS.has(routeLocationSlug)
        : false;
      const normalizedRouteLocation = isReservedRouteLocation
        ? undefined
        : routeLocationSlug;
      const routeActivitySlug = normalizedRouteLocation
        ? parsedPath.activitySlug
        : undefined;
      const storedListingLocation = getStoredListingLocationSlug(locale);

      const locationSlug = resolveLocationSlug(pending, {
        routeLocation: normalizedRouteLocation,
        storedListingLocation,
        fallbackArea,
        locale: locale as LocaleType,
      });
      const activitySlug = resolveActivitySlug(
        pending.activities,
        routeActivitySlug,
      );

      const nextParams = redirectToHome
        ? new URLSearchParams()
        : preservedParams(params);

      const finalUrl = buildDiscoverUrl({
        locale: locale as LocaleType,
        locationSlug,
        activitySlug,
        query: nextParams,
      });

      appliedRef.current = pendingSearch.get();
      resetAllTriggers();

      startTransition(() => {
        router.push(finalUrl);
      });
    },
    [
      params,
      pathname,
      router,
      locale,
      fallbackArea,
      resetAllTriggers,
      redirectToHome,
    ],
  );

  return { apply, isPending };
}
